<script setup>
import {reactive,onMounted,computed} from "vue";
import Prism from 'prismjs';
import WorkflowChart from 'vue-workflow-chart';
import Vjsf from "@koumoul/vjsf";
import {PrismEditor} from "vue-prism-editor";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";

const data = reactive({
  dummyFormModel: {},
  formTab: 'tab-preview',
  selectedSchema: null,
  selectedWorkflow: null,
  selectedTask: null,
  stateSemantics: [{
    "classname": "select",
    "id": "AssignAdjuster",
  }],
  size: { width: '0px', height: '0px' },
  workflowData: {
    schema_options: [],
    workflow_options: [],
    workflows: {}
  }
});

const formCode =computed(()=>{
    return JSON.stringify(JSON.parse(data.selectedTask.formJsonSchema), null, 2)
});

const isValidInterval = computed(()=>{
  return typeof data.selectedTask.intervalAllotted === 'string'
});

const intervalAllottedValue = computed(()=>{
  if (data.selectedTask.intervalAllotted) {
    let val = data.selectedTask.intervalAllotted.split(' ')
    return val[0]
  }
  return 1000;
});

const intervalAllottedInterval = computed(()=>{
  if (data.selectedTask.intervalAllotted) {
    let val = data.selectedTask.intervalAllotted.split(' ')
    return val[1]
  }
  return 'days';
});

const fetchWorkflowData = () => {
  ApiWrapper.post('tasks','list-workflows',{})
      .then((response) => {
        data.workflowData = response
      })
      .catch(err => {
        ToastUtil.error(err);
      })
}

const highlighter = (code) => {
  // js highlight example
  return Prism.highlight(code, Prism.languages.js, "js");
}

const onLabelClicked = (type, id) => {
  data.selectedTask = data.workflowData.workflows[data.selectedWorkflow].states.filter(tt => tt.label === id)[0]
  data.stateSemantics = [{
    "classname": "selected",
    "id": id,
  }]
}

const selectSchema = (schemaId) => {
  data.selectedSchema = schemaId
  data.selectedWorkflow = null
  data.selectedTask = null
}

const selectWorkflow = (workflowId) => {
  data.selectedWorkflow = workflowId
  data.selectedTask = null
}

const sizeChanged = (size) => {
  data.size = {
    width: `${size.width}px`,
    height: `${size.height}px`,
  };
}

onMounted(()=>{
  fetchWorkflowData()
})
</script>

<template>
  <v-container fluid>

    <v-card>
      <v-toolbar flat color="white">
        <v-toolbar-title>Workflow Editor</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>

        <v-spacer></v-spacer>
        <v-btn variant="elevated" color="primary" size="small" disabled>Add Workflow</v-btn>

      </v-toolbar>
      <v-container>
        <v-row>
          <v-col>
            <v-list-subheader>Schemas</v-list-subheader>
            <v-divider></v-divider>
            <v-list-item class="v-list--select-list">
              <v-list-item v-for="schema in data.workflowData.schema_options" :key="schema">
                <v-list-item @click="selectSchema(schema)">
                  <v-list-item-title>{{schema}}</v-list-item-title>
                </v-list-item>
              </v-list-item>
            </v-list-item>
          </v-col>
          <v-col>
            <v-list-subheader>Workflows</v-list-subheader>
            <v-divider></v-divider>
            <v-list-item class="v-list--select-list">
              <v-list-item v-for="workflow in data.workflowData.workflow_options[data.selectedSchema]" :key="workflow">
                <v-list-item @click="selectWorkflow(workflow.value)">
                  <v-list-item-title>{{workflow.text}}</v-list-item-title>
                </v-list-item>
              </v-list-item>
            </v-list-item>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-card v-if="data.selectedWorkflow" class="card--graph">
      <v-container>
        <v-row>
          <v-col>
            <workflow-chart ref="graph" :style="data.size"
                            :transitions="data.workflowData.workflows[data.selectedWorkflow].transitions"
                            :states="data.workflowData.workflows[data.selectedWorkflow].states" v-bind:state-semantics="data.stateSemantics"
                            :orientation="'vertical'" @state-click="onLabelClicked('state',$event)"
                            @transition-click="onLabelClicked('transition', $event)" @size-change="sizeChanged" />
          </v-col>
          <v-col>
            <v-form readonly v-if="data.selectedTask">
              <v-text-field label="Task Name" v-model="data.selectedTask.name" outlined></v-text-field>
              <div v-if="isValidInterval">
                <v-slider label="Time Allotted" :value="intervalAllottedValue" thumb-color="blue" thumb-label="always"
                          min="1" max="100">
                </v-slider>
                <v-select label="Interval" :value="intervalAllottedInterval" :items="['hours', 'days', 'weeks']">
                </v-select>
              </div>
              <v-card v-if="data.selectedTask.formJsonSchema" class="my-5">
                <v-card-title>Form Preview</v-card-title>
                <v-tabs v-model="data.formTab">

                  <v-tab href="#tab-preview">
                    Preview
                  </v-tab>

                  <v-tab href="#tab-code">
                    Code
                  </v-tab>

                  <v-window v-model="data.formTab">
                    <v-window-item value="tab-preview">
                      <v-card flat class="pa-5">
                        <v-form>
                          <vjsf v-model="data.dummyFormModel" :schema="JSON.parse(data.selectedTask.formJsonSchema)" />
                        </v-form>
                      </v-card>
                    </v-window-item>

                    <v-window-item value="tab-code">
                      <v-card flat class="px-5">
                        <prism-editor class="my-editor height-300" v-model="formCode" :highlight="highlighter"
                                      line-numbers></prism-editor>
                      </v-card>
                    </v-window-item>
                  </v-window>

                </v-tabs>
              </v-card>

              <v-btn variant="elevated" color="primary" disabled>Save</v-btn>
            </v-form>
          </v-col>
        </v-row>
      </v-container>

    </v-card>
  </v-container>
</template>

<style scoped>
.vue-workflow-chart-state-selected {
  color: white;
  background: #90CAF9;
}

.card--graph {
  /* FIXME: how to make height responsive */
  min-height: 2000px;
}

.card--graph .container {
  max-width: 100%;
}

/* For vue-prism-editor */
.height-300 {
  height: 300px
}

.my-editor {
  /* we dont use `language-` classes anymore so thats why we need to add background and text color manually */
  background: #2d2d2d;
  color: #ccc;

  /* you must provide font-family font-size line-height. Example:*/
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
}

/* optional class for removing the outline */
.prism-editor__textarea:focus {
  outline: none;
}
</style>